<script setup>
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

defineProps({
  orders: {
    type: Array,
    default: () => [],
  },
  visibleColumns: {
    type: Array,
    default: () => [
      'externalOrderId',
      'shopName', 
      'buyerName',
      'source',
      'status',
      'totalCents',
      'paidAt',
      'placedAt',
      'isGift',
      'actions'
    ],
  },
});

const emit = defineEmits(['takecare']);

const router = useRouter();
const { t } = useI18n();

const getInboxById = useMapGetter('inboxes/getInboxById');

const sourceIcon = source => {
  const mapping = {
    etsy: 'storefront',
    tiktok: 'video',
    shopee: 'shopping-bag',
    lazada: 'shopping-bag',
  };
  return mapping[source] || 'shopping-bag';
};

const statusColor = status => {
  const mapping = {
    pending: 'gray',
    processing: 'blue',
    shipped: 'orange',
    delivered: 'green',
    cancelled: 'red',
    refunded: 'red',
  };
  return mapping[status] || 'gray';
};

const goTo = order => {
  router.push({
    name: 'orders_details',
    params: { orderId: order.id },
  });
};

const convertToHumanReadableDate = ts => {
  if (!ts || ts === 0) return '-';
  const date = new Date(ts * 1000);
  return date.toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatPrice = (cents, currency = 'USD') => {
  if (!cents || Number.isNaN(cents)) return '-';
  return `${(cents / 100).toFixed(2)} ${currency}`;
};

const hasDiscount = order => {
  return order.discountCents && order.discountCents > 0;
};

const inboxName = order => {
  const inbox = getInboxById.value(order.inboxId);
  return inbox?.name || order.shopName || order.externalShopId || '-';
};

const onTakecare = order => {
  emit('takecare', order);
};

// Column configuration
const columnConfig = {
  externalOrderId: {
    label: 'TABLE.ORDER_ID',
    width: 'min-w-[120px]',
    align: 'left',
    render: (item) => `#${item.externalOrderId || '-'}`
  },
  shopName: {
    label: 'TABLE.SHOP',
    width: 'min-w-[120px]',
    align: 'left',
    render: (item) => inboxName(item)
  },
  buyerName: {
    label: 'TABLE.BUYER',
    width: 'min-w-[150px]',
    align: 'left',
    render: (item) => ({
      primary: item.buyerName || '-',
      secondary: item.buyerEmail
    })
  },
  source: {
    label: 'TABLE.SOURCE',
    width: 'min-w-[100px]',
    align: 'left',
    render: (item) => ({
      icon: sourceIcon(item.source),
      text: item.source
    })
  },
  status: {
    label: 'TABLE.STATUS',
    width: 'min-w-[110px]',
    align: 'left',
    render: (item) => ({
      text: item.status,
      color: statusColor(item.status)
    })
  },
  totalCents: {
    label: 'TABLE.TOTAL',
    width: 'min-w-[100px]',
    align: 'right',
    render: (item) => ({
      primary: formatPrice(item.totalCents, item.currencyCode),
      secondary: hasDiscount(item) ? `-${formatPrice(item.discountCents, item.currencyCode)} ${t('TABLE.DISCOUNT_SUFFIX')}.` : null
    })
  },
  paidAt: {
    label: 'TABLE.PAYMENT_STATUS',
    width: 'min-w-[130px]',
    align: 'left',
    render: (item) => ({
      isPaid: item.paidAt && item.paidAt > 0,
      paidDate: item.paidAt ? convertToHumanReadableDate(item.paidAt) : null
    })
  },
  placedAt: {
    label: 'TABLE.PLACED_DATE',
    width: 'min-w-[110px]',
    align: 'left',
    render: (item) => convertToHumanReadableDate(item.placedAt)
  },
  isGift: {
    label: 'TABLE.TAGS',
    width: 'min-w-[100px]',
    align: 'left',
    render: (item) => ({
      isGift: item.isGift,
      hasDiscount: hasDiscount(item)
    })
  },
  currencyCode: {
    label: 'TABLE.CURRENCY',
    width: 'min-w-[80px]',
    align: 'left',
    render: (item) => item.currencyCode || 'USD'
  },
  shippingCents: {
    label: 'TABLE.SHIPPING',
    width: 'min-w-[100px]',
    align: 'right',
    render: (item) => formatPrice(item.shippingCents, item.currencyCode)
  },
  taxCents: {
    label: 'TABLE.TAX',
    width: 'min-w-[80px]',
    align: 'right',
    render: (item) => formatPrice(item.taxCents, item.currencyCode)
  },
  discountCents: {
    label: 'TABLE.DISCOUNT',
    width: 'min-w-[100px]',
    align: 'right',
    render: (item) => formatPrice(item.discountCents, item.currencyCode)
  },
  fulfilledAt: {
    label: 'TABLE.FULFILLED_DATE',
    width: 'min-w-[130px]',
    align: 'left',
    render: (item) => convertToHumanReadableDate(item.fulfilledAt)
  },
  expectedDeliveryDate: {
    label: 'TABLE.EXPECTED_DELIVERY',
    width: 'min-w-[130px]',
    align: 'left',
    render: (item) => convertToHumanReadableDate(item.expectedDeliveryDate)
  },
  noteFromBuyer: {
    label: 'TABLE.NOTE',
    width: 'min-w-[150px]',
    align: 'left',
    render: (item) => item.noteFromBuyer || '-'
  },
  giftMessage: {
    label: 'TABLE.GIFT_MESSAGE',
    width: 'min-w-[150px]',
    align: 'left',
    render: (item) => item.giftMessage || '-'
  },
  orderUrl: {
    label: 'TABLE.ORDER_URL',
    width: 'min-w-[120px]',
    align: 'left',
    render: (item) => item.orderUrl ? 'View' : '-'
  },
  actions: {
    label: 'TABLE.ACTIONS',
    width: 'min-w-[140px]',
    align: 'left',
    render: () => 'takecare'
  }
};

const getColumnConfig = (columnKey) => {
  return columnConfig[columnKey] || { label: columnKey, width: 'min-w-[100px]', align: 'left' };
};

const isColumnVisible = (columnKey) => {
  return props.visibleColumns.includes(columnKey);
};
</script>

<template>
  <div class="w-full">
    <div class="w-[90%] mx-auto overflow-x-auto">
      <div
        class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm"
      >
        <table class="w-full">
          <thead class="bg-slate-50 dark:bg-slate-800">
            <tr>
              <th
                v-for="columnKey in visibleColumns"
                :key="columnKey"
                :class="[
                  'px-4 py-3 text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider',
                  getColumnConfig(columnKey).width,
                  getColumnConfig(columnKey).align === 'right' ? 'text-right' : 'text-left'
                ]"
              >
                {{ t(getColumnConfig(columnKey).label) }}
              </th>
            </tr>
          </thead>
          <tbody
            class="bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-700"
          >
            <tr
              v-for="item in orders"
              :key="item.id"
              class="hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
              @click="goTo(item)"
            >
              <td
                v-for="columnKey in visibleColumns"
                :key="columnKey"
                :class="[
                  'px-4 py-4 whitespace-nowrap',
                  getColumnConfig(columnKey).align === 'right' ? 'text-right' : 'text-left'
                ]"
                @click.stop="columnKey === 'actions' ? null : goTo(item)"
              >
                <!-- Order ID -->
                <div
                  v-if="columnKey === 'externalOrderId'"
                  class="text-sm font-medium text-woot-500 hover:text-woot-600"
                >
                  {{ getColumnConfig(columnKey).render(item) }}
                </div>

                <!-- Shop Name -->
                <div
                  v-else-if="columnKey === 'shopName'"
                  class="text-sm text-slate-900 dark:text-slate-100"
                >
                  {{ getColumnConfig(columnKey).render(item) }}
                </div>

                <!-- Buyer Name -->
                <div
                  v-else-if="columnKey === 'buyerName'"
                >
                  <div class="text-sm text-slate-900 dark:text-slate-100">
                    {{ getColumnConfig(columnKey).render(item).primary }}
                  </div>
                  <div
                    v-if="getColumnConfig(columnKey).render(item).secondary"
                    class="text-xs text-slate-500"
                  >
                    {{ getColumnConfig(columnKey).render(item).secondary }}
                  </div>
                </div>

                <!-- Source -->
                <div
                  v-else-if="columnKey === 'source'"
                  class="flex items-center"
                >
                  <fluent-icon
                    :icon="getColumnConfig(columnKey).render(item).icon"
                    size="16"
                    class="text-slate-600 dark:text-slate-400"
                  />
                  <span
                    class="ml-2 text-sm text-slate-900 dark:text-slate-100 capitalize"
                  >
                    {{ getColumnConfig(columnKey).render(item).text }}
                  </span>
                </div>

                <!-- Status -->
                <div
                  v-else-if="columnKey === 'status'"
                >
                  <span
                    class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                    :class="{
                      'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200':
                        getColumnConfig(columnKey).render(item).color === 'gray',
                      'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200':
                        getColumnConfig(columnKey).render(item).color === 'blue',
                      'bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-200':
                        getColumnConfig(columnKey).render(item).color === 'orange',
                      'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200':
                        getColumnConfig(columnKey).render(item).color === 'green',
                      'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200':
                        getColumnConfig(columnKey).render(item).color === 'red',
                    }"
                  >
                    {{ t(`STATUS_${getColumnConfig(columnKey).render(item).text.toUpperCase()}`) }}
                  </span>
                </div>

                <!-- Total -->
                <div
                  v-else-if="columnKey === 'totalCents'"
                >
                  <div
                    class="text-sm text-slate-900 dark:text-slate-100 font-medium"
                  >
                    {{ getColumnConfig(columnKey).render(item).primary }}
                  </div>
                  <div
                    v-if="getColumnConfig(columnKey).render(item).secondary"
                    class="text-xs text-red-600 dark:text-red-400"
                  >
                    {{ getColumnConfig(columnKey).render(item).secondary }}
                  </div>
                </div>

                <!-- Payment Status -->
                <div
                  v-else-if="columnKey === 'paidAt'"
                >
                  <div class="text-sm text-slate-900 dark:text-slate-100">
                    <span
                      v-if="getColumnConfig(columnKey).render(item).isPaid"
                      class="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200 rounded-full"
                    >
                      <fluent-icon icon="checkmark" size="12" class="mr-1" />
                      {{ t('PAYMENT.PAID') }}
                    </span>
                    <span
                      v-else
                      class="inline-flex items-center px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-200 rounded-full"
                    >
                      <fluent-icon icon="clock" size="12" class="mr-1" />
                      {{ t('PAYMENT.PENDING') }}
                    </span>
                  </div>
                  <div
                    v-if="getColumnConfig(columnKey).render(item).paidDate"
                    class="text-xs text-slate-500 mt-1"
                  >
                    {{ getColumnConfig(columnKey).render(item).paidDate }}
                  </div>
                </div>

                <!-- Placed Date -->
                <div
                  v-else-if="columnKey === 'placedAt'"
                  class="text-sm text-slate-600 dark:text-slate-400"
                >
                  {{ getColumnConfig(columnKey).render(item) }}
                </div>

                <!-- Tags -->
                <div
                  v-else-if="columnKey === 'isGift'"
                  class="flex flex-wrap gap-1"
                >
                  <span
                    v-if="getColumnConfig(columnKey).render(item).isGift"
                    class="inline-flex items-center px-2 py-1 text-xs font-medium bg-pink-100 text-pink-800 dark:bg-pink-800 dark:text-pink-200 rounded-full"
                  >
                    <fluent-icon icon="gift" size="12" class="mr-1" />
                    {{ t('TAGS.GIFT') }}
                  </span>
                  <span
                    v-if="getColumnConfig(columnKey).render(item).hasDiscount"
                    class="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200 rounded-full"
                  >
                    <fluent-icon icon="tag" size="12" class="mr-1" />
                    {{ t('TAGS.SALE') }}
                  </span>
                </div>

                <!-- Actions -->
                <div
                  v-else-if="columnKey === 'actions'"
                >
                  <woot-button size="small" icon="chat" @click="onTakecare(item)">
                    {{ t('TABLE.TAKECARE') }}
                  </woot-button>
                </div>

                <!-- Other columns -->
                <div
                  v-else
                  class="text-sm text-slate-900 dark:text-slate-100"
                >
                  {{ getColumnConfig(columnKey).render(item) }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
